import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { CountryCode } from 'shared/constants/countries';
import OrganisationAccountings from 'shared/db/services/OrganisationAccountings';
import { ExternalAccountingProviderType } from 'shared/types/Invoicing';

import { useDatabase } from '@/hooks';
import { useRegion } from '@/hooks/useRegion';
import { databaseValidation } from '@/services/databaseValidation';
import Logger from '@/services/logger';

export type ContextValue = {
    isInvoicingAvailable: boolean;
    isInvoiceSystemEnabled: boolean;
};

export type Props = {
    children: React.ReactNode;
};

const InvoicingSystemContext = createContext<ContextValue>({
    isInvoicingAvailable: false,
    isInvoiceSystemEnabled: false,
});

const InvoicingSystemContextProvider = ({ children }: Props) => {
    const [accountingProvider, setAccountingProvider] =
        useState<ExternalAccountingProviderType | null>();

    const db = useDatabase();
    const database = useMemo(() => db.getDatabase(), [db]);
    const region = useRegion();
    const isUSRegion = region?.code === CountryCode.unitedStates;
    const isInvoiceSystemEnabled = isUSRegion;

    const accountingsService = useMemo(
        () =>
            new OrganisationAccountings({
                database,
                logDBAction: Logger.logRecordActivity,
                validation: databaseValidation,
            }),
        [database],
    );

    useEffect(() => {
        accountingsService
            .get()
            .observeWithColumns(['accounting_provider'])
            .subscribe(([accounting]) => {
                setAccountingProvider(accounting?.accountingProvider);
            });
    }, [accountingsService]);
    return (
        <InvoicingSystemContext.Provider
            value={{
                isInvoicingAvailable:
                    !isInvoiceSystemEnabled || Boolean(accountingProvider),
                isInvoiceSystemEnabled,
            }}
        >
            {children}
        </InvoicingSystemContext.Provider>
    );
};

function useInvoicingSystemContext() {
    const context = useContext(InvoicingSystemContext);

    return context;
}

export { InvoicingSystemContextProvider, useInvoicingSystemContext };
